/**
 * @license
 * The widget's Lucide glyphs — the starter glyphs and the row arrow here,
 * the composer's send arrow (arrow-up, steward-input.ts) and the menu check
 * (steward-chat.ts) — are Lucide 1.43.0 (https://lucide.dev). Of them, lock,
 * calendar, database, download, life-buoy, search, trash-2, upload,
 * circle-help, arrow-right, arrow-up and check derive from Feather.
 * SPDX-License-Identifier: ISC AND MIT
 *
 * ISC License
 *
 * Copyright (c) 2026 Lucide Icons and Contributors
 *
 * Permission to use, copy, modify, and/or distribute this software for any
 * purpose with or without fee is hereby granted, provided that the above
 * copyright notice and this permission notice appear in all copies.
 *
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
 * WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
 * ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
 * WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
 * ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
 * OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 *
 * The MIT License (MIT) (for the icons listed above)
 *
 * Copyright (c) 2013-present Cole Bemis
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

/**
 * Starter questions: up to three questions an operator writes for the
 * welcome state, each a row that sends its question when tapped.
 *
 * Everything here is pure: what an operator supplies is read, validated
 * and cut to what renders, and a glyph name is resolved to its drawing.
 * Rendering and the send live in steward-chat.ts.
 *
 * The glyphs are static Lit `svg` templates, so drawing one parses no
 * markup at run time and fetches nothing, and no operator string can reach
 * the drawing: a name only selects among these.
 */
import { svg, type SVGTemplateResult } from 'lit';

/** One starter as an operator writes it, in the attribute's JSON or the
 *  property's array. `icon` names a glyph from STARTER_GLYPH_NAMES. */
export interface StarterInput {
  question: string;
  hint?: string;
  icon?: string;
}

/** One starter as the widget renders it: trimmed, validated, and with an
 *  `icon` only when the operator named one. */
export interface Starter {
  question: string;
  hint?: string;
  icon?: string;
}

/** At most this many rows render; the rest of a longer list is ignored. */
export const MAX_STARTERS = 3;

/** A question longer than this, after trimming, makes its entry invalid. */
export const MAX_QUESTION_LENGTH = 120;

/** The glyph a row draws when its entry names none, or a name outside the
 *  set, while another row names one. */
export const FALLBACK_GLYPH = 'circle-help';

const GLYPHS: Record<string, SVGTemplateResult> = {
  'key-round': svg`<path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z" /><circle cx="16.5" cy="7.5" r=".5" fill="currentColor" />`,
  'lock': svg`<rect width="18" height="11" x="3" y="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />`,
  'shield-check': svg`<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /><path d="m9 12 2 2 4-4" />`,
  'user': svg`<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />`,
  'users': svg`<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><path d="M16 3.128a4 4 0 0 1 0 7.744" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><circle cx="9" cy="7" r="4" />`,
  'credit-card': svg`<rect width="20" height="14" x="2" y="5" rx="2" /><line x1="2" x2="22" y1="10" y2="10" />`,
  'receipt': svg`<path d="M12 17V7" /><path d="M16 8h-6a2 2 0 0 0 0 4h4a2 2 0 0 1 0 4H8" /><path d="M4 3a1 1 0 0 1 1-1 1.3 1.3 0 0 1 .7.2l.933.6a1.3 1.3 0 0 0 1.4 0l.934-.6a1.3 1.3 0 0 1 1.4 0l.933.6a1.3 1.3 0 0 0 1.4 0l.933-.6a1.3 1.3 0 0 1 1.4 0l.934.6a1.3 1.3 0 0 0 1.4 0l.933-.6A1.3 1.3 0 0 1 19 2a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1 1.3 1.3 0 0 1-.7-.2l-.933-.6a1.3 1.3 0 0 0-1.4 0l-.934.6a1.3 1.3 0 0 1-1.4 0l-.933-.6a1.3 1.3 0 0 0-1.4 0l-.933.6a1.3 1.3 0 0 1-1.4 0l-.934-.6a1.3 1.3 0 0 0-1.4 0l-.933.6a1.3 1.3 0 0 1-.7.2 1 1 0 0 1-1-1z" />`,
  'wallet': svg`<path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" /><path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" />`,
  'settings': svg`<path d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915" /><circle cx="12" cy="12" r="3" />`,
  'plug': svg`<path d="M12 22v-5" /><path d="M15 8V2" /><path d="M17 8a1 1 0 0 1 1 1v4a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1z" /><path d="M9 8V2" />`,
  'database': svg`<ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5V19A9 3 0 0 0 21 19V5" /><path d="M3 12A9 3 0 0 0 21 12" />`,
  'file-text': svg`<path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" /><path d="M14 2v5a1 1 0 0 0 1 1h5" /><path d="M10 9H8" /><path d="M16 13H8" /><path d="M16 17H8" />`,
  'book-open': svg`<path d="M12 5v16" /><path d="M20.001 19A2 2 0 0022 17V5a2 2 0 00-1.999-2L16 3.002A5 5 0 0012 5a5 5 0 00-4-2H4a2 2 0 00-2 2v12a2 2 0 001.999 2H8a5 5 0 014 2 5 5 0 014-2z" />`,
  'search': svg`<path d="m21 21-4.34-4.34" /><circle cx="11" cy="11" r="8" />`,
  'trash-2': svg`<path d="M10 11v6" /><path d="M14 11v6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />`,
  'upload': svg`<path d="M12 3v12" /><path d="m17 8-5-5-5 5" /><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />`,
  'download': svg`<path d="M12 15V3" /><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="m7 10 5 5 5-5" />`,
  'rocket': svg`<path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" /><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09" /><path d="M9 12a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.4 22.4 0 0 1-4 2z" /><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 .05 5 .05" />`,
  'zap': svg`<path d="M15.914 4a1.5 1.5 0 00-2.474-1.561l-9 9A1.5 1.5 0 005.5 14h4.002a.5.5 0 01.471.666L8.086 20a1.5 1.5 0 002.475 1.56l9-9A1.5 1.5 0 0018.5 10h-3.997a.5.5 0 01-.472-.667z" />`,
  'bell': svg`<path d="M10.268 21a2 2 0 0 0 3.464 0" /><path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />`,
  'calendar': svg`<path d="M8 2v3" /><path d="M16 2v3" /><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" />`,
  'globe': svg`<circle cx="12" cy="12" r="10" /><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" /><path d="M2 12h20" />`,
  'life-buoy': svg`<circle cx="12" cy="12" r="10" /><path d="m4.93 4.93 4.24 4.24" /><path d="m14.83 9.17 4.24-4.24" /><path d="m14.83 14.83 4.24 4.24" /><path d="m9.17 14.83-4.24 4.24" /><circle cx="12" cy="12" r="4" />`,
  'circle-help': svg`<circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><path d="M12 17h.01" />`,
};

/** The names an operator may give a starter's `icon`, in the set's order. */
export const STARTER_GLYPH_NAMES: readonly string[] = Object.freeze(Object.keys(GLYPHS));

/** The row's trailing arrow. */
export const ROW_ARROW: SVGTemplateResult = svg`<path d="M5 12h14" /><path d="m12 5 7 7-7 7" />`;

/** The drawing for a glyph name: the named glyph, or the fallback for a
 *  name outside the set. An own-property check, so a name that happens to
 *  be an Object.prototype member ('constructor', '__proto__') is outside it
 *  too. */
export function starterGlyph(name: string | undefined): SVGTemplateResult {
  return name !== undefined && Object.hasOwn(GLYPHS, name) ? GLYPHS[name] : GLYPHS[FALLBACK_GLYPH];
}

/** The `starters` attribute's value, read as JSON. Text that is not JSON
 *  yields null, with a warning naming the attribute; it never throws, so a
 *  typo in the host page's markup cannot take the page down with it. */
export function parseStartersAttribute(value: string | null): unknown {
  if (value === null) return null;
  try {
    return JSON.parse(value);
  } catch {
    console.warn('[steward-chat] starters is not valid JSON; no starter questions are shown.');
    return null;
  }
}

const nonEmpty = (v: unknown): string | undefined => {
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  return t === '' ? undefined : t;
};

/** The starters to render from whatever the attribute or property holds:
 *  the first three valid entries, in order. An entry is valid when it is an
 *  object whose `question` is a string of 1 to 120 characters after
 *  trimming. A `hint` or `icon` that is not a non-empty string is dropped
 *  from its entry; an `icon` outside the set is kept, so the row still
 *  draws the fallback and the all-or-none rule sees that it was named.
 *  Anything that is not an array yields no starters. */
export function normalizeStarters(value: unknown): Starter[] {
  if (!Array.isArray(value)) return [];
  const out: Starter[] = [];
  for (const entry of value) {
    if (out.length === MAX_STARTERS) break;
    if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) continue;
    const { question, hint, icon } = entry as Record<string, unknown>;
    const q = nonEmpty(question);
    if (q === undefined || q.length > MAX_QUESTION_LENGTH) continue;
    const starter: Starter = { question: q };
    const h = nonEmpty(hint);
    if (h !== undefined) starter.hint = h;
    const i = nonEmpty(icon);
    if (i !== undefined) starter.icon = i;
    out.push(starter);
  }
  return out;
}

/** All or none: rows draw glyphs only when at least one of them names one,
 *  and then every row draws one. */
export function startersShowGlyphs(starters: readonly Starter[]): boolean {
  return starters.some((s) => s.icon !== undefined);
}
